import React, { useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import QuoteDetailModal from '../Quote/QuoteDetailModal';
import OrderDetailModal from '../Order/OrderDetailModal';
import OpenProcurementVariantCell from './OpenProcurementVariantCell';
import OpenProcurementStatusCell from './OpenProcurementStatusCell';
import { logInfo } from '../../config_and_helpers/logger';
import {
  getOpenQuotes,
  getActiveOpenOrders,
  hasOpenProcurement,
  isCatalogFamilyMatch,
  getProcurementHighlightLevel,
  getOpenQuoteProcurementStatus,
  getOpenOrderProcurementStatus,
  getOpenOrderLineReceptionStatus,
  formatOpenOrderQuantityLabel,
} from '../../utils/productOpenProcurement';
import './ProductComponentStyle.css';

const LOG_CONTEXT = 'ProductOpenProcurementSection';

/**
 * Shows in-flight quotes and orders for a product from API fields
 * `open_quotes` and `open_orders` on product detail. Hidden when both are empty.
 *
 * Status columns: quotes use `status`; orders lead with `order_item_status` (this
 * product's line) and show `quote.status` as a secondary order-stage hint.
 *
 * @param {Object} props
 * @param {Object} props.product - Product detail payload from get_details
 * @param {boolean} [props.isSupplier]
 * @param {function} [props.clearSearchValue] - Passed to nested detail modals
 */
const ProductOpenProcurementSection = ({
  product,
  isSupplier = false,
  clearSearchValue = () => {},
}) => {
  const openQuotes = getOpenQuotes(product);
  const openOrders = getActiveOpenOrders(product);
  const openQuotesCount = openQuotes.length;
  const openOrdersCount = openOrders.length;
  const catalogFamilyQuoteMatches = openQuotes.filter(isCatalogFamilyMatch).length;
  const catalogFamilyOrderMatches = openOrders.filter(isCatalogFamilyMatch).length;
  const highlightLevel = getProcurementHighlightLevel(product);

  useEffect(() => {
    if (!product?.id || (openQuotesCount === 0 && openOrdersCount === 0)) return;
    logInfo(LOG_CONTEXT, 'Open procurement section rendered', {
      productId: product.id,
      openQuotesCount,
      openOrdersCount,
      catalogFamilyQuoteMatches,
      catalogFamilyOrderMatches,
      highlightLevel,
    });
  }, [
    product?.id,
    openQuotesCount,
    openOrdersCount,
    catalogFamilyQuoteMatches,
    catalogFamilyOrderMatches,
    highlightLevel,
  ]);

  if (!hasOpenProcurement(product)) {
    return null;
  }

  const subtitle =
    highlightLevel === 'order'
      ? 'In-flight order(s) linked to this product.'
      : 'In-flight quote(s) linked to this product.';

  return (
    <div
      className={`open-procurement-callout open-procurement-callout--${highlightLevel}`}
    >
      <h5 className="open-procurement-callout__title fw-bold mb-0">
        Open procurement
      </h5>
      <p className="open-procurement-callout__subtitle text-muted mb-0">
        {subtitle}
      </p>

      {openQuotes.length > 0 && (
        <div className="mb-4 mt-3">
          <h6 className="mb-2 fw-semibold">Quotes</h6>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="text-center align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Demand ref</th>
                  <th>Status</th>
                  <th>Qty</th>
                  <th>Variant</th>
                </tr>
              </thead>
              <tbody>
                {openQuotes.map(row => (
                  <tr key={`open-quote-${row.id}`}>
                    <td>
                      <QuoteDetailModal
                        quoteId={row.id}
                        clearSearchValue={clearSearchValue}
                      />
                    </td>
                    <td>{row.corporate_demand_ref ?? '—'}</td>
                    <td>
                      <OpenProcurementStatusCell
                        status={getOpenQuoteProcurementStatus(row)}
                      />
                    </td>
                    <td>{row.quantity ?? '—'}</td>
                    <td>
                      <OpenProcurementVariantCell
                        row={row}
                        isSupplier={isSupplier}
                        clearSearchValue={clearSearchValue}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {openOrders.length > 0 && (
        <div className="mb-0 mt-3">
          <h6 className="mb-2 fw-semibold">Orders</h6>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="text-center align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Order ref</th>
                  <th>Status</th>
                  <th>Qty</th>
                  <th>Variant</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.map(row => {
                  const lineReception = getOpenOrderLineReceptionStatus(row);
                  const procurementStatus = getOpenOrderProcurementStatus(row);
                  const primaryStatus = lineReception ?? procurementStatus;
                  const secondaryStatus = lineReception ? procurementStatus : null;
                  return (
                    <tr key={`open-order-${row.id}`}>
                      <td>
                        <OrderDetailModal
                          orderId={row.id}
                          clearSearchValue={clearSearchValue}
                        />
                      </td>
                      <td>{row.corporate_order_ref ?? '—'}</td>
                      <td>
                        <OpenProcurementStatusCell status={primaryStatus} />
                        {secondaryStatus && (
                          <div className="mt-1">
                            <span className="text-muted small">Order: </span>
                            <OpenProcurementStatusCell
                              status={secondaryStatus}
                              muted
                            />
                          </div>
                        )}
                      </td>
                      <td>{formatOpenOrderQuantityLabel(row)}</td>
                      <td>
                        <OpenProcurementVariantCell
                          row={row}
                          isSupplier={isSupplier}
                          clearSearchValue={clearSearchValue}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductOpenProcurementSection;
